import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { BusinessEmailLimitsApiService } from '../services/business-email-limits-api.service';
import {
  BusinessEmailLimit,
  UpdateBusinessEmailLimitRequest,
  UserEmailChannelType,
} from '../models/business-email-limit.model';

/** Estado editable local de cada tarjeta de canal. */
interface ChannelRow {
  channelType: UserEmailChannelType;
  label: string;
  hasOverride: boolean;
  globalDailyLimit: number | null;
  globalPerMinuteLimit: number | null;
  // Valores en edición (number inputs).
  dailyLimit: number | null;
  perMinuteLimit: number | null;
  burst: number | null;
  enabled: boolean;
  saving: boolean;
  resetting: boolean;
}

@Component({
  selector: 'app-business-email-limits',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    BadgeComponent,
  ],
  templateUrl: './business-email-limits.component.html',
})
export class BusinessEmailLimitsComponent implements OnInit {
  rows: ChannelRow[] = [];
  loading = false;
  error = '';

  private readonly channelLabels: Record<UserEmailChannelType, string> = {
    USER_SMTP: 'SMTP',
    USER_GMAIL: 'Gmail',
    USER_MICROSOFT: 'Outlook',
    USER_API: 'API',
  };

  constructor(
    private api: BusinessEmailLimitsApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargar();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.api.list().subscribe({
      next: (data) => {
        this.rows = (data ?? []).map((d) => this.toRow(d));
        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.message || 'Error cargando los límites de envío';
        this.loading = false;
      },
    });
  }

  private toRow(d: BusinessEmailLimit): ChannelRow {
    return {
      channelType: d.channelType,
      label: this.channelLabels[d.channelType] ?? d.channelType,
      hasOverride: d.hasOverride,
      globalDailyLimit: d.globalDailyLimit,
      globalPerMinuteLimit: d.globalPerMinuteLimit,
      dailyLimit: d.dailyLimit,
      perMinuteLimit: d.perMinuteLimit,
      burst: d.burst,
      enabled: d.enabled,
      saving: false,
      resetting: false,
    };
  }

  /** Mensaje de error de validación cliente (o null si la fila es válida). */
  validationError(row: ChannelRow): string | null {
    if (row.dailyLimit === null || row.dailyLimit === undefined) {
      return 'El límite diario es requerido.';
    }
    if (row.perMinuteLimit === null || row.perMinuteLimit === undefined) {
      return 'El límite por minuto es requerido.';
    }
    if (row.dailyLimit < 0 || row.perMinuteLimit < 0 || (row.burst !== null && row.burst < 0)) {
      return 'Los valores no pueden ser negativos.';
    }
    if (row.globalDailyLimit !== null && row.dailyLimit > row.globalDailyLimit) {
      return `El límite diario no puede superar el global (${row.globalDailyLimit}).`;
    }
    if (row.globalPerMinuteLimit !== null && row.perMinuteLimit > row.globalPerMinuteLimit) {
      return `El límite por minuto no puede superar el global (${row.globalPerMinuteLimit}).`;
    }
    return null;
  }

  canSave(row: ChannelRow): boolean {
    return !row.saving && !row.resetting && this.validationError(row) === null;
  }

  guardar(row: ChannelRow): void {
    if (!this.canSave(row)) return;

    const req: UpdateBusinessEmailLimitRequest = {
      dailyLimit: row.dailyLimit as number,
      perMinuteLimit: row.perMinuteLimit as number,
      burst: row.burst,
      enabled: row.enabled,
    };

    row.saving = true;
    this.api.upsert(row.channelType, req).subscribe({
      next: (updated) => {
        row.saving = false;
        this.replaceRow(updated);
        this.toast.updated('Límite de envío');
      },
      error: (err) => {
        row.saving = false;
        this.toast.error({
          title: 'Error',
          message: err?.error?.message || 'Error al guardar el límite de envío',
        });
      },
    });
  }

  async restablecer(row: ChannelRow): Promise<void> {
    if (!row.hasOverride || row.saving || row.resetting) return;

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Restablecer al global',
      message: `¿Restablecer el canal "${row.label}" al límite global? Se eliminará la personalización de tu empresa.`,
      confirmText: 'Restablecer',
    });
    if (!ok) return;

    row.resetting = true;
    this.api.resetToGlobal(row.channelType).subscribe({
      next: () => {
        row.resetting = false;
        this.toast.success('Se restableció el canal al límite global');
        this.cargar();
      },
      error: (err) => {
        row.resetting = false;
        this.toast.error({
          title: 'Error',
          message: err?.error?.message || 'Error al restablecer el límite de envío',
        });
      },
    });
  }

  private replaceRow(updated: BusinessEmailLimit): void {
    const idx = this.rows.findIndex((r) => r.channelType === updated.channelType);
    if (idx >= 0) {
      this.rows[idx] = this.toRow(updated);
    }
  }

  trackByChannel = (_: number, row: ChannelRow) => row.channelType;
}
